/**
 * croniq — a tiny, zero-dependency cron expression toolkit.
 *
 * Parse standard 5-field (and 6-field "with seconds") cron expressions, then
 * compute the next/previous run times and a human-readable description. The
 * time arithmetic uses field-jumping (not minute-by-minute scanning), so even
 * sparse schedules resolve quickly.
 */

export interface CronOptions {
  /** Interpret and emit times in UTC instead of local time. Default: `false`. */
  utc?: boolean;
}

const MONTH_NAMES = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const DOW_NAMES = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DOW_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MACROS: Record<string, string> = {
  "@yearly": "0 0 1 1 *",
  "@annually": "0 0 1 1 *",
  "@monthly": "0 0 1 * *",
  "@weekly": "0 0 * * 0",
  "@daily": "0 0 * * *",
  "@midnight": "0 0 * * *",
  "@hourly": "0 * * * *",
};

interface FieldSpec {
  name: string;
  min: number;
  max: number; // value used to generate "*"
  accept: number; // highest accepted literal (dow accepts 7)
  names?: string[];
}

const LIMIT = 500_000; // safety bound for field-jump loops

function resolveName(token: string, spec: FieldSpec): number {
  const lower = token.toLowerCase();
  if (spec.names) {
    const idx = spec.names.indexOf(lower);
    if (idx !== -1) return idx + (spec.name === "month" ? 1 : 0);
  }
  if (!/^\d+$/.test(token)) {
    throw new SyntaxError(`croniq: invalid value "${token}" in ${spec.name} field`);
  }
  return Number(token);
}

function parseField(raw: string, spec: FieldSpec): { values: number[]; restricted: boolean } {
  const normalize = (v: number): number => (spec.name === "dayOfWeek" && v === 7 ? 0 : v);

  if (raw === "*" || raw === "?") {
    const values: number[] = [];
    for (let v = spec.min; v <= spec.max; v++) values.push(v);
    return { values, restricted: false };
  }

  const out = new Set<number>();
  for (const part of raw.split(",")) {
    const [rangePart, stepPart] = part.split("/") as [string, string | undefined];
    const step = stepPart === undefined ? 1 : Number(stepPart);
    if (!Number.isInteger(step) || step < 1) {
      throw new SyntaxError(`croniq: invalid step "${stepPart}" in ${spec.name} field`);
    }

    let lo: number;
    let hi: number;
    if (rangePart === "*") {
      lo = spec.min;
      hi = spec.max;
    } else if (rangePart.includes("-")) {
      const [a, b] = rangePart.split("-") as [string, string];
      lo = resolveName(a, spec);
      hi = resolveName(b, spec);
    } else {
      lo = resolveName(rangePart, spec);
      hi = stepPart === undefined ? lo : spec.max;
    }

    for (const bound of [lo, hi]) {
      if (bound < spec.min || bound > spec.accept) {
        throw new RangeError(
          `croniq: value ${bound} out of range (${spec.min}-${spec.accept}) in ${spec.name} field`,
        );
      }
    }
    if (lo > hi) {
      throw new RangeError(`croniq: range start ${lo} > end ${hi} in ${spec.name} field`);
    }
    for (let v = lo; v <= hi; v += step) out.add(normalize(v));
  }

  return { values: [...out].sort((a, b) => a - b), restricted: true };
}

interface Accessor {
  year(d: Date): number;
  month0(d: Date): number; // 0-based
  date(d: Date): number;
  hours(d: Date): number;
  minutes(d: Date): number;
  seconds(d: Date): number;
  dow(d: Date): number;
  setMonth0(d: Date, v: number): void;
  setDate(d: Date, v: number): void;
  setHours(d: Date, v: number): void;
  setMinutes(d: Date, v: number): void;
  setSeconds(d: Date, v: number): void;
}

const localAccessor: Accessor = {
  year: (d) => d.getFullYear(),
  month0: (d) => d.getMonth(),
  date: (d) => d.getDate(),
  hours: (d) => d.getHours(),
  minutes: (d) => d.getMinutes(),
  seconds: (d) => d.getSeconds(),
  dow: (d) => d.getDay(),
  setMonth0: (d, v) => void d.setMonth(v),
  setDate: (d, v) => void d.setDate(v),
  setHours: (d, v) => void d.setHours(v),
  setMinutes: (d, v) => void d.setMinutes(v),
  setSeconds: (d, v) => void d.setSeconds(v),
};

const utcAccessor: Accessor = {
  year: (d) => d.getUTCFullYear(),
  month0: (d) => d.getUTCMonth(),
  date: (d) => d.getUTCDate(),
  hours: (d) => d.getUTCHours(),
  minutes: (d) => d.getUTCMinutes(),
  seconds: (d) => d.getUTCSeconds(),
  dow: (d) => d.getUTCDay(),
  setMonth0: (d, v) => void d.setUTCMonth(v),
  setDate: (d, v) => void d.setUTCDate(v),
  setHours: (d, v) => void d.setUTCHours(v),
  setMinutes: (d, v) => void d.setUTCMinutes(v),
  setSeconds: (d, v) => void d.setUTCSeconds(v),
};

/** A parsed cron expression. Create one with {@link parse} (or `new Cron(...)`). */
export class Cron {
  readonly expression: string;
  readonly hasSeconds: boolean;
  readonly seconds: number[];
  readonly minutes: number[];
  readonly hours: number[];
  readonly daysOfMonth: number[];
  readonly months: number[];
  readonly daysOfWeek: number[];
  /** Raw field tokens (after macro expansion), for description. */
  readonly raw: { seconds?: string; minute: string; hour: string; dayOfMonth: string; month: string; dayOfWeek: string };
  private readonly domRestricted: boolean;
  private readonly dowRestricted: boolean;
  private readonly utc: boolean;

  constructor(expression: string, options: CronOptions = {}) {
    this.utc = options.utc ?? false;
    const trimmed = expression.trim();
    const expanded = MACROS[trimmed.toLowerCase()] ?? trimmed;
    const parts = expanded.split(/\s+/);

    if (parts.length !== 5 && parts.length !== 6) {
      throw new SyntaxError(
        `croniq: expected 5 or 6 fields, got ${parts.length} in "${expression}"`,
      );
    }

    this.hasSeconds = parts.length === 6;
    const [secRaw, minRaw, hourRaw, domRaw, monthRaw, dowRaw] = this.hasSeconds
      ? (parts as [string, string, string, string, string, string])
      : (["0", ...parts] as unknown as [string, string, string, string, string, string]);

    const sec = parseField(secRaw, { name: "second", min: 0, max: 59, accept: 59 });
    const min = parseField(minRaw, { name: "minute", min: 0, max: 59, accept: 59 });
    const hour = parseField(hourRaw, { name: "hour", min: 0, max: 23, accept: 23 });
    const dom = parseField(domRaw, { name: "dayOfMonth", min: 1, max: 31, accept: 31 });
    const month = parseField(monthRaw, { name: "month", min: 1, max: 12, accept: 12, names: MONTH_NAMES });
    const dow = parseField(dowRaw, { name: "dayOfWeek", min: 0, max: 6, accept: 7, names: DOW_NAMES });

    this.seconds = sec.values;
    this.minutes = min.values;
    this.hours = hour.values;
    this.daysOfMonth = dom.values;
    this.months = month.values;
    this.daysOfWeek = dow.values;
    this.domRestricted = dom.restricted;
    this.dowRestricted = dow.restricted;
    this.expression = trimmed;
    this.raw = {
      ...(this.hasSeconds ? { seconds: secRaw } : {}),
      minute: minRaw,
      hour: hourRaw,
      dayOfMonth: domRaw,
      month: monthRaw,
      dayOfWeek: dowRaw,
    };
  }

  private acc(): Accessor {
    return this.utc ? utcAccessor : localAccessor;
  }

  private dayMatches(d: Date, a: Accessor): boolean {
    const dm = this.daysOfMonth.includes(a.date(d));
    const dw = this.daysOfWeek.includes(a.dow(d));
    if (this.domRestricted && this.dowRestricted) return dm || dw;
    if (this.domRestricted) return dm;
    if (this.dowRestricted) return dw;
    return true;
  }

  /** Does `date` satisfy this expression (to minute, or second, precision)? */
  matches(date: Date = new Date()): boolean {
    const a = this.acc();
    if (!this.months.includes(a.month0(date) + 1)) return false;
    if (!this.dayMatches(date, a)) return false;
    if (!this.hours.includes(a.hours(date))) return false;
    if (!this.minutes.includes(a.minutes(date))) return false;
    if (this.hasSeconds) return this.seconds.includes(a.seconds(date));
    return a.seconds(date) === 0;
  }

  /** The next run time strictly after `from` (default: now). Throws if none within ~bounds. */
  next(from: Date = new Date()): Date {
    return this.step(from, 1);
  }

  /** The previous run time strictly before `from` (default: now). */
  prev(from: Date = new Date()): Date {
    return this.step(from, -1);
  }

  /** The next `count` run times after `from`. */
  nextN(count: number, from: Date = new Date()): Date[] {
    const out: Date[] = [];
    let cursor = from;
    for (let i = 0; i < count; i++) {
      cursor = this.next(cursor);
      out.push(cursor);
    }
    return out;
  }

  /** The previous `count` run times before `from`. */
  prevN(count: number, from: Date = new Date()): Date[] {
    const out: Date[] = [];
    let cursor = from;
    for (let i = 0; i < count; i++) {
      cursor = this.prev(cursor);
      out.push(cursor);
    }
    return out;
  }

  private daysInMonth(d: Date, a: Accessor): number {
    // Day 0 of next month = last day of this month.
    const probe = new Date(d.getTime());
    a.setDate(probe, 1);
    a.setMonth0(probe, a.month0(probe) + 1);
    a.setDate(probe, 0);
    return a.date(probe);
  }

  private step(from: Date, dir: 1 | -1): Date {
    const a = this.acc();
    const secMax = this.hasSeconds ? 59 : 0;
    const unit = this.hasSeconds ? 1000 : 60_000;

    // Start strictly past `from`, snapped to the resolution boundary.
    let t = Math.floor(from.getTime() / unit) * unit + dir * unit;
    const d = new Date(t);
    if (!this.hasSeconds) a.setSeconds(d, 0);

    for (let iter = 0; iter < LIMIT; iter++) {
      if (!this.months.includes(a.month0(d) + 1)) {
        if (dir === 1) {
          a.setDate(d, 1);
          a.setHours(d, 0);
          a.setMinutes(d, 0);
          a.setSeconds(d, 0);
          a.setMonth0(d, a.month0(d) + 1);
        } else {
          a.setDate(d, 1);
          a.setMonth0(d, a.month0(d) - 1);
          a.setDate(d, this.daysInMonth(d, a));
          a.setHours(d, 23);
          a.setMinutes(d, 59);
          a.setSeconds(d, secMax);
        }
        continue;
      }
      if (!this.dayMatches(d, a)) {
        if (dir === 1) {
          a.setHours(d, 0);
          a.setMinutes(d, 0);
          a.setSeconds(d, 0);
          a.setDate(d, a.date(d) + 1);
        } else {
          a.setHours(d, 23);
          a.setMinutes(d, 59);
          a.setSeconds(d, secMax);
          a.setDate(d, a.date(d) - 1);
        }
        continue;
      }
      if (!this.hours.includes(a.hours(d))) {
        if (dir === 1) {
          a.setMinutes(d, 0);
          a.setSeconds(d, 0);
          a.setHours(d, a.hours(d) + 1);
        } else {
          a.setMinutes(d, 59);
          a.setSeconds(d, secMax);
          a.setHours(d, a.hours(d) - 1);
        }
        continue;
      }
      if (!this.minutes.includes(a.minutes(d))) {
        if (dir === 1) {
          a.setSeconds(d, 0);
          a.setMinutes(d, a.minutes(d) + 1);
        } else {
          a.setSeconds(d, secMax);
          a.setMinutes(d, a.minutes(d) - 1);
        }
        continue;
      }
      if (this.hasSeconds && !this.seconds.includes(a.seconds(d))) {
        a.setSeconds(d, a.seconds(d) + dir);
        continue;
      }
      return new Date(d.getTime());
    }
    throw new RangeError(`croniq: no matching time found for "${this.expression}" within bounds`);
  }

  /** A human-readable English description, e.g. "At 09:00, Monday through Friday". */
  describe(): string {
    const a = this.raw;
    const two = (n: number) => n.toString().padStart(2, "0");

    let time: string;
    const everyMin = a.minute === "*";
    const everyHour = a.hour === "*";
    if (this.hasSeconds && a.seconds === "*" && everyMin) {
      time = "every second";
    } else if (everyMin && everyHour) {
      time = "every minute";
    } else if (this.minutes.length === 1 && this.hours.length === 1) {
      time = `at ${two(this.hours[0] as number)}:${two(this.minutes[0] as number)}`;
    } else if (everyHour && this.minutes.length === 1) {
      time = `at ${this.minutes[0]} minutes past every hour`;
    } else if (a.minute.startsWith("*/")) {
      time = `every ${a.minute.slice(2)} minutes`;
    } else {
      time = `at minute ${listText(this.minutes)} past hour ${listText(this.hours)}`;
    }

    const parts = [time];

    if (this.dowRestricted) {
      parts.push(dowText(this.daysOfWeek));
    }
    if (this.domRestricted) {
      parts.push(`on day-of-month ${listText(this.daysOfMonth)}`);
    }
    if (a.month !== "*") {
      parts.push(`in ${this.months.map((m) => MONTH_LABELS[m - 1]).join(", ")}`);
    }

    return capitalize(parts.join(", "));
  }
}

function listText(values: number[]): string {
  if (values.length === 1) return String(values[0]);
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return values.slice(0, -1).join(", ") + ", and " + values[values.length - 1];
}

function dowText(values: number[]): string {
  // Detect a contiguous run for nicer "X through Y" phrasing.
  const sorted = [...values].sort((a, b) => a - b);
  const contiguous = sorted.every((v, i) => i === 0 || v === (sorted[i - 1] as number) + 1);
  if (contiguous && sorted.length > 2) {
    return `${DOW_LABELS[sorted[0] as number]} through ${DOW_LABELS[sorted[sorted.length - 1] as number]}`;
  }
  return sorted.map((d) => DOW_LABELS[d]).join(", ");
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Parse a cron expression into a {@link Cron} you can query.
 *
 * ```ts
 * const cron = parse("0 9 * * 1-5");      // 09:00 on weekdays
 * cron.next();                            // → next weekday 09:00
 * cron.describe();                        // "At 09:00, Monday through Friday"
 * ```
 *
 * Supports 5 fields (min hour dom month dow) or 6 (with a leading seconds
 * field), ranges (`1-5`), lists (`1,3,5`), steps (`*​/15`), names (`JAN`,
 * `MON`), `?`, and macros (`@daily`, `@hourly`, …).
 */
export function parse(expression: string, options?: CronOptions): Cron {
  return new Cron(expression, options);
}

/** Returns `true` if `expression` is a valid cron expression. */
export function isValid(expression: string, options?: CronOptions): boolean {
  try {
    new Cron(expression, options);
    return true;
  } catch {
    return false;
  }
}
